import { api } from "@/lib/api-client";
import type { Paginated } from "../agents/api";
import type { ToolCallData } from "../agents/components/ToolCallBlock";

export type StructuredSummary = {
  caller_name?: string;
  intent?: string;
  outcome?: string;
  summary?: string;
  next_steps?: string[];
  key_facts?: string[];
  follow_up_required?: boolean;
  tags?: string[];
};

export type Call = {
  id: string;
  from_number: string;
  to_number: string;
  agent_name?: string | null;
  /** Set on Assistant calls (agent is null) — read from call metadata. */
  assistant_name?: string | null;
  agent?: { id: string; name: string } | null;
  /** Set when an outside app placed the call with an API key: the key's name. */
  api_key_name?: string | null;
  /** Raw call metadata; assistant calls carry structured_outputs here. */
  metadata?: Record<string, unknown> | null;
  status: "completed" | "failed" | "in_progress" | "no_answer" | "pending" | string;
  direction?: "inbound" | "outbound" | string;
  /** Call channel: "whatsapp" for WhatsApp Business calls, else "phone". */
  channel?: "whatsapp" | "phone" | string;
  duration_seconds: number;
  started_at: string | null;
  ended_at?: string | null;
  created_at?: string;
  sentiment?: "positive" | "neutral" | "negative" | null;
  sentiment_score?: number | null;
  recording_url?: string | null;
  transcript?: string | null;
  summary?: string | null;
  summary_status?: "pending" | "processing" | "completed" | "failed" | string;
  structured_summary?: StructuredSummary | null;
  crm_push_status?: "" | "pending" | "success" | "failed";
  crm_pushed_at?: string | null;
  crm_push_target?: string;
  crm_push_error?: string;
  crm_external_id?: string;
  lead_score?: number | null;
  lead_grade?: "hot" | "warm" | "cold" | "";
  lead_reasoning?: string;
  buying_signals?: string[];
  lead_score_status?:
    | "pending"
    | "processing"
    | "completed"
    | "failed"
    | "disabled"
    | string;
  followup_status?:
    | "pending"
    | "processing"
    | "sent"
    | "skipped"
    | "failed"
    | "disabled"
    | string;
  followup_channels?: string[];
  followup_sent_at?: string | null;
  followup_error?: string;
  hot_lead_routed_at?: string | null;
  hot_lead_routing_channels?: string[];
  hot_lead_routing_error?: string;
};

export type LeadScoreSettings = {
  enabled: boolean;
  custom_criteria: string;
  hot_threshold: number;
  warm_threshold: number;
};

export type CallSummaryPayload = {
  id: string;
  summary_status: string;
  summary: string;
  structured_summary: StructuredSummary;
  sentiment: string;
  sentiment_score: number | null;
  crm_push_status: "" | "pending" | "success" | "failed";
  crm_pushed_at: string | null;
  crm_push_target: string;
  crm_push_error: string;
  crm_external_id: string;
};

export type CallTranscript = {
  turns?: Array<{
    role?: "user" | "assistant";
    speaker?: "user" | "agent";
    text?: string;
    content?: string;
    /** Tool/function calls the agent ran on this turn (real calls only). */
    tool_calls?: ToolCallData[];
  }>;
};

export type CallLatencyTurn = {
  turn_index: number;
  user_message: string;
  assistant_response: string;
  e2e_ms: number;
  llm_ms: number;
  tts_ms: number;
  total_ms: number;
  /** Per-stage waterfall (v4 pipeline; 0 on older v3 rows). */
  endpointing_ms: number;
  stt_ms: number;
  llm_ttft_ms: number;
  tts_ttfb_ms: number;
  tool_ms: number;
  pipeline_version: string;
};

export type CallLatency = {
  summary: {
    turn_count: number;
    avg_e2e_ms: number;
    p50_e2e_ms: number;
    p95_e2e_ms: number;
    max_e2e_ms: number;
    avg_llm_ms: number;
    avg_tts_ms: number;
  };
  turns: CallLatencyTurn[];
};

export type CallStats = {
  total_calls: number;
  total_duration_seconds: number;
  success_rate?: number;
  by_status?: Record<string, number>;
};

export const callsApi = {
  // Don't expose `ordering` here by default — DRF's OrderingFilter is on
  // globally, and passing it reorders the result vs. the model default
  // ('-created_at'). Add it back explicitly when a screen needs it.
  list: async (params?: {
    limit?: number;
    ordering?: string;
    page?: number;
    status?: string;
    lead_grade?: "hot" | "warm" | "cold";
  }) => {
    const res = await api.get<Paginated<Call> | Call[]>("/calls/", { params });
    return Array.isArray(res.data) ? res.data : res.data.results;
  },

  stats: async () => {
    const res = await api.get<CallStats>("/calls/stats/");
    return res.data;
  },

  get: async (id: string) => {
    const res = await api.get<Call>(`/calls/${id}/`);
    return res.data;
  },

  transcript: async (id: string) => {
    const res = await api.get<CallTranscript>(`/calls/${id}/transcript/`);
    return res.data;
  },

  latency: async (id: string) => {
    const res = await api.get<CallLatency>(`/calls/${id}/latency/`);
    return res.data;
  },

  summary: async (id: string) => {
    const res = await api.get<CallSummaryPayload>(`/calls/${id}/summary/`);
    return res.data;
  },

  generateSummary: async (id: string) => {
    const res = await api.post<{ status: string }>(
      `/calls/${id}/generate-summary/`,
    );
    return res.data;
  },

  pushToCrm: async (id: string, integrationId: string) => {
    const res = await api.post<{ status: string }>(
      `/calls/${id}/push-to-crm/`,
      { integration_id: integrationId },
    );
    return res.data;
  },

  generateLeadScore: async (id: string) => {
    const res = await api.post<{ status: string }>(
      `/calls/${id}/generate-lead-score/`,
    );
    return res.data;
  },

  sendFollowup: async (id: string) => {
    const res = await api.post<{
      followup_status: string;
      followup_channels: string[];
      followup_sent_at: string | null;
      followup_error: string;
    }>(`/calls/${id}/send-followup/`);
    return res.data;
  },
};

export const leadScoringSettingsApi = {
  get: async () => {
    const res = await api.get<LeadScoreSettings>("/tenants/lead-scoring/");
    return res.data;
  },
  update: async (data: Partial<LeadScoreSettings>) => {
    const res = await api.patch<LeadScoreSettings>(
      "/tenants/lead-scoring/",
      data,
    );
    return res.data;
  },
};
